import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MaintenanceSchedulesService } from '../services/schedules.service';
import type { 
  MaintenanceSchedule, 
  ScheduleFilters 
} from '../types/maintenance-schedules.types';
import { useState } from 'react';

export function useMaintenanceSchedules(companyId: string, initialFilters: ScheduleFilters = {}) {
  return useSchedules(companyId, initialFilters);
}

export function useMaintenanceScheduleStats(companyId: string) {
  return useQuery({
    queryKey: ['maintenance-schedules-stats', companyId],
    queryFn: () => MaintenanceSchedulesService.getScheduleStats(companyId),
    enabled: !!companyId,
  });
}

export function useSchedules(companyId: string, initialFilters: ScheduleFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ScheduleFilters>({
    status: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['maintenance-schedules', companyId, filters],
    queryFn: () => MaintenanceSchedulesService.getSchedules(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['maintenance-schedules-stats', companyId],
    queryFn: () => MaintenanceSchedulesService.getScheduleStats(companyId),
    enabled: !!companyId,
  });

  const createScheduleMutation = useMutation({
    mutationFn: (data: Partial<MaintenanceSchedule>) => MaintenanceSchedulesService.createSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-schedules-stats'] });
    },
  });

  const updateScheduleMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<MaintenanceSchedule> }) => 
      MaintenanceSchedulesService.updateSchedule(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-schedules', 'detail', variables.id] });
    },
  });

  const generateWOMutation = useMutation({
    mutationFn: (scheduleId: string) => MaintenanceSchedulesService.generateWorkOrder(scheduleId),
    onSuccess: (_, scheduleId) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-schedules', 'detail', scheduleId] });
    },
  });

  return {
    ...query,
    data: query.data?.data || [],
    schedules: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    filters,
    setFilters,
    createSchedule: createScheduleMutation.mutateAsync,
    updateSchedule: updateScheduleMutation.mutateAsync,
    generateWorkOrder: generateWOMutation.mutateAsync,
    isCreating: createScheduleMutation.isPending,
    isUpdating: updateScheduleMutation.isPending,
    isGeneratingWO: generateWOMutation.isPending,
  };
}

export function useScheduleDetail(id?: string) {
  return useQuery({
    queryKey: ['maintenance-schedules', 'detail', id],
    queryFn: () => MaintenanceSchedulesService.getScheduleDetail(id!),
    enabled: !!id,
  });
}
